import { ArrowDown, ArrowUp, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BLOCK_TYPE_LABELS,
  BLOCK_TYPE_ORDER,
  resolveBlueprintBlockRef,
  type ComposerBlock,
  type ComposerBlockType,
  type ComposerBlueprintBlockRef,
} from "@/lib/composerBlocks";
import type { EditorState } from "./useBlueprintsPanel";

type Props = {
  editor: EditorState;
  setEditor: React.Dispatch<React.SetStateAction<EditorState>>;
  blocks: ComposerBlock[];
  blocksByType: Map<ComposerBlockType, ComposerBlock[]>;
  emptyEditor: EditorState;
  onSave: () => void;
  isSaving: boolean;
  canSave: boolean;
  addRef: () => void;
  updateRef: (idx: number, patch: Partial<ComposerBlueprintBlockRef>) => void;
  removeRef: (idx: number) => void;
  moveRef: (idx: number, dir: -1 | 1) => void;
};

export const BlueprintEditorDialog = ({
  editor,
  setEditor,
  blocks,
  blocksByType,
  emptyEditor,
  onSave,
  isSaving,
  canSave,
  addRef,
  updateRef,
  removeRef,
  moveRef,
}: Props) => (
  <Dialog open={editor.open} onOpenChange={(open) => setEditor((s) => ({ ...s, open }))}>
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{editor.editingId ? "Edit blueprint" : "New blueprint"}</DialogTitle>
        <DialogDescription>
          Order block slots top-to-bottom. Pick a specific block, or leave it as "any" to fall back to the first block of that type when dragged into a draft.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-3">
        <div className="grid gap-1.5">
          <Label htmlFor="bp-title">Title</Label>
          <Input
            id="bp-title"
            value={editor.title}
            onChange={(e) => setEditor((s) => ({ ...s, title: e.target.value }))}
            placeholder="Standard newsletter"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="bp-desc">Description</Label>
          <Textarea
            id="bp-desc"
            rows={2}
            value={editor.description}
            onChange={(e) => setEditor((s) => ({ ...s, description: e.target.value }))}
            placeholder="Hook → body → CTA → close → signature"
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="bp-hint">Content type hint (optional)</Label>
          <Input
            id="bp-hint"
            value={editor.contentTypeHint}
            onChange={(e) => setEditor((s) => ({ ...s, contentTypeHint: e.target.value }))}
            placeholder="newsletter, essay, article…"
          />
        </div>

        <div className="grid gap-2">
          <div className="flex items-center justify-between">
            <Label>Block stack</Label>
            <Button size="sm" variant="outline" onClick={addRef}>
              <Plus className="h-3.5 w-3.5" />
              Add slot
            </Button>
          </div>
          <div className="space-y-2">
            {editor.blockRefs.map((ref, idx) => {
              const typed = blocksByType.get(ref.type) ?? [];
              const resolved = resolveBlueprintBlockRef(ref, blocks);
              const previewLines = (resolved?.body ?? "").split("\n").slice(0, 6).join("\n");
              return (
                <div key={idx} className="rounded-md border border-border/60 bg-card p-2 space-y-2">
                  <div className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-2">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-sm bg-muted text-xs font-medium tabular-nums text-muted-foreground">
                      {idx + 1}
                    </span>
                    <Select
                      value={ref.type}
                      onValueChange={(v) =>
                        updateRef(idx, { type: v as ComposerBlockType, blockId: undefined })
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BLOCK_TYPE_ORDER.map((t) => (
                          <SelectItem key={t} value={t}>
                            {BLOCK_TYPE_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={ref.blockId ?? "__any"}
                      onValueChange={(v) => updateRef(idx, { blockId: v === "__any" ? undefined : v })}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Any block of this type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__any">Any block of this type</SelectItem>
                        {typed.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => moveRef(idx, -1)}
                        disabled={idx === 0}
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => moveRef(idx, 1)}
                        disabled={idx === editor.blockRefs.length - 1}
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={() => removeRef(idx)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {resolved ? (
                    <div className="rounded-sm bg-muted/40 px-2 py-1.5">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-medium truncate">{resolved.title}</span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1">
                          {BLOCK_TYPE_LABELS[resolved.type]}
                        </Badge>
                      </div>
                      <pre className="text-[11px] leading-snug font-mono whitespace-pre-wrap text-muted-foreground line-clamp-6">
                        {previewLines}
                      </pre>
                    </div>
                  ) : (
                    <p className="text-[11px] text-muted-foreground italic px-1">
                      No matching block in this workspace yet — slot will render as a placeholder.
                    </p>
                  )}
                </div>
              );
            })}
            {editor.blockRefs.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Add at least one slot to save the blueprint.
              </p>
            )}
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="ghost" onClick={() => setEditor(emptyEditor)}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={!canSave || isSaving}>
          {editor.editingId ? "Save changes" : "Create blueprint"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
